import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../../../services/auth.service';
import { ApiService } from '../../../../services/api.service';
import { BroadcastService } from '../../../../services/broadcast.service';
import { User, UserBadge } from '../../../../models/user';
import { Observable, Subscription } from 'rxjs';
import { ConfigService, UserItems } from '../../../../services/config.service';
import { InfoButton } from '../../../../models/client-configuration';
import { BreakpointObserver } from '@angular/cdk/layout';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { UserEventsService } from '../../../../services/user-events/user-events.service';
import { DatePipe } from '@angular/common';
import { HeaderService } from '../../services/header.service';
import { UserConfigFacade } from 'src/app/state/user-config/user-config.facade';
import { HelpButtonDialogFacade } from 'src/app/_core/state/dialogs/help-button/help-button.facade';

@Component({
  selector: 'app-header-user-button',
  templateUrl: './header-user-button.component.html',
  styleUrls: ['./header-user-button.component.scss'],
})
export class HeaderUserButtonComponent implements OnInit, OnDestroy {

  private readonly helpButtonDialogFacade = inject(HelpButtonDialogFacade);

  private static readonly TABLET_RESIZE = '(max-width: 768px)';

  private breakpointObserverSubscription: Subscription;
  private profilePictureSubscription: Subscription;
  private userEventsCountSubscription: Subscription;
  private userNotificationsSubscription: Subscription;

  helpUrl: string | null = null;
  infoButtons: InfoButton[] = [];
  isSignUpAllowed: boolean = false;
  isUserAuthenticated: boolean = false;
  isUserGuest: boolean = false;
  loggedUser: User = null;
  showButtonBadge: boolean = false;
  showHelpButtonsInMenu: boolean = false;
  userEventsCount: number = 0;
  userAvatar$: Observable<UserBadge>;
  userItems: UserItems[] = [];
  isPendingNotifications: boolean;

  constructor(
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly broadcastService: BroadcastService,
    private readonly configService: ConfigService,
    private readonly datePipe: DatePipe,
    private readonly headerService: HeaderService,
    private readonly userEventsService: UserEventsService,
    private readonly userConfigFacade: UserConfigFacade
  ) { }

  ngOnInit(): void {
    const configuration = this.configService.get();

    this.userEventsCountSubscription = this.userEventsService.userEvents$
      .pipe(filter((value) => value !== null))
      .subscribe((userEvents) => {
        const currentDayWithFormat = String(this.datePipe.transform(new Date(), 'yyyy-MM-dd'));

        this.userEventsCount = userEvents.dayEvents.filter((event) => {
          return (
            this.datePipe.transform(new Date(event.date), 'yyyy-MM-dd') === currentDayWithFormat &&
            event.events.length > 0
          );
        }).length;
      });

    this.breakpointObserverSubscription = this.breakpointObserver
      .observe(HeaderUserButtonComponent.TABLET_RESIZE)
      .pipe(distinctUntilChanged())
      .subscribe((matched) => {
        this.showButtonBadge = matched.matches;
        this.showHelpButtonsInMenu = matched.matches;
      });

    this.showHelpButtonsInMenu = this.breakpointObserver.isMatched(HeaderUserButtonComponent.TABLET_RESIZE);

    this.helpUrl = configuration.helpUrl;
    this.infoButtons = configuration.infoButtons;
    this.isSignUpAllowed = configuration.allowSignUp;
    this.isUserAuthenticated = this.authService.isAuthenticated() ?? false;
    this.isUserGuest = configuration.inviteGuestAccountActive && configuration.customLoginUrl !== null;
    this.loggedUser = this.authService.getLoggedUser();
    this.profilePictureSubscription = this.broadcastService.profilePicture$.subscribe(() => this.loadAvatarProfile());
    this.userItems = this.configService.userItems;

    if (this.isUserAuthenticated) {
      this.loadAvatarProfile();

      this.userNotificationsSubscription = this.userConfigFacade.pendingNotifications$.subscribe(
        (pendingNotifications) => this.isPendingNotifications = pendingNotifications);
    }
  }

  ngOnDestroy() {
    this.breakpointObserverSubscription?.unsubscribe();
    this.profilePictureSubscription?.unsubscribe();
    this.userNotificationsSubscription?.unsubscribe();
    this.userEventsCountSubscription?.unsubscribe();
  }

  handleLogin() {
    this.headerService.login();
  }

  handleLoginGuest() {
    this.headerService.guestLogin();
  }

  handleSignUp() {
    this.headerService.signUp();
  }

  handleHelp() {
    this.headerService.openHelp();
  }

  handleInfoButton(button: InfoButton) {
    if (button.link) {
      window.open(button.link);
      return;
    }

    this.helpButtonDialogFacade.openDialog(button.textButton, button.textContent);
  }

  private loadAvatarProfile() {
    this.userAvatar$ = this.apiService.getUserPicture(this.loggedUser.id);
  }
}
